/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import {
  Auth0Client,
  IdToken,
  LogoutOptions,
  RedirectLoginOptions as Auth0RedirectLoginOptions,
  GetTokenSilentlyOptions,
  GetIdTokenClaimsOptions,
  RedirectLoginResult,
} from "@auth0/auth0-spa-js";
import { initialAuthState } from "./auth-state";
import Auth0Context, { RedirectLoginOptions } from "./auth0-context";
import { reducer } from "./reducer";
import { hasAuthParams, tokenError } from "./utils";

/**
 * The state of the application before the user was redirected to the login page.
 */
export type AppState = { [key: string]: Record<string, unknown> } & {
  returnTo?: string;
};

/**
 * The main configuration to instantiate the `Auth0Provider`.
 */
export type Auth0ProviderOptions = {
  /**
   * The child nodes your Provider has wrapped
   */
  children?: React.ReactNode;
  /**
   * Auth0 Client
   */
  client: Auth0Client;
  /**
   * By default this removes the code and state parameters from the url when you are redirected from the authorize page.
   * It uses `window.history` but you might want to overwrite this if you are using a custom router, like `react-router-dom`
   * See the EXAMPLES.md for more info.
   */
  onRedirectCallback?: (appState: AppState) => void;
  /**
   * By default, if the page url has code/state params, the SDK will treat them as Auth0's and attempt to exchange the
   * code for a token. In some cases the code might be for something else (another OAuth SDK perhaps). In these
   * instances you can instruct the client to ignore them eg
   *
   * ```jsx
   * <Auth0Provider
   *   clientId={clientId}
   *   domain={domain}
   *   skipRedirectCallback={window.location.pathname === '/stripe-oauth-callback'}
   * >
   * ```
   */
  skipRedirectCallback?: boolean;
};

/**
 * @ignore
 */
const toAuth0LoginRedirectOptions = (
  opts?: RedirectLoginOptions
): Auth0RedirectLoginOptions | undefined => {
  if (!opts) {
    return;
  }
  const { redirectUri, ...validOpts } = opts;

  // eslint-disable-next-line consistent-return
  return {
    ...validOpts,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    redirect_uri: redirectUri,
  };
};

/**
 * @ignore
 */
const defaultOnRedirectCallback = (appState?: AppState): void => {
  window.history.replaceState(
    {},
    document.title,
    appState?.returnTo || window.location.pathname
  );
};

/**
 * ```jsx
 * <Auth0Provider
 *   client={auth0Client}>
 *   <MyApp />
 * </Auth0Provider>
 * ```
 *
 * Provides the Auth0Context to its child components.
 */
function Auth0Provider({
  children,
  client,
  skipRedirectCallback,
  onRedirectCallback = defaultOnRedirectCallback,
}: Auth0ProviderOptions): JSX.Element {
  const [state, dispatch] = React.useReducer(reducer, initialAuthState);

  const fetchUser = React.useCallback(async () => {
    const authenticated = await client.isAuthenticated();

    if (authenticated) {
      return client.getUser();
    }
  }, [client]);

  React.useEffect(() => {
    const initAuth0 = async () => {
      if (hasAuthParams() && !skipRedirectCallback) {
        const { appState } = await client.handleRedirectCallback();
        onRedirectCallback(appState);
      }

      await client.checkSession();
      const user = await fetchUser();
      dispatch({ type: "INITIALISED", user });
    };
    initAuth0();
  }, [client, onRedirectCallback, skipRedirectCallback, fetchUser]);

  const loginWithRedirect = React.useCallback(
    (loginRedirectOpts?: RedirectLoginOptions): Promise<void> =>
      client.loginWithRedirect(toAuth0LoginRedirectOptions(loginRedirectOpts)),
    [client]
  );

  const logout = React.useCallback(
    (logoutOpts: LogoutOptions = {}): void => {
      client.logout(logoutOpts);
      if (logoutOpts.localOnly) {
        dispatch({ type: "LOGOUT" });
      }
    },
    [client]
  );

  const getAccessTokenSilently = React.useCallback(
    async (
      getAccessTokenSilentlyOpts?: GetTokenSilentlyOptions
    ): Promise<string> => {
      // eslint-disable-next-line fp/no-let
      let token;
      try {
        // eslint-disable-next-line fp/no-mutation
        token = await client.getTokenSilently(getAccessTokenSilentlyOpts);
      } catch (error) {
        throw tokenError(error);
      } finally {
        dispatch({
          type: "GET_ACCESS_TOKEN_COMPLETE",
          user: await client.getUser(),
        });
      }

      return token;
    },
    [client]
  );

  const getIdTokenClaims = React.useCallback(
    (claimsOpts?: GetIdTokenClaimsOptions): Promise<IdToken> =>
      client.getIdTokenClaims(claimsOpts),
    [client]
  );

  const handleRedirectCallback = React.useCallback(
    async (url?: string): Promise<RedirectLoginResult> => {
      try {
        return await client.handleRedirectCallback(url);
      } catch (error) {
        throw tokenError(error);
      } finally {
        dispatch({
          type: "HANDLE_REDIRECT_COMPLETE",
          user: await client.getUser(),
        });
      }
    },
    [client]
  );

  return (
    <Auth0Context.Provider
      value={{
        ...state,
        getAccessTokenSilently,
        getIdTokenClaims,
        loginWithRedirect,
        logout,
        handleRedirectCallback,
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
}

export default Auth0Provider;
